import * as uuid from "uuid";
import * as yup from "yup";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Input,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import {
  CreateJobRequest,
  GroundPrepJobConfiguration,
  Job,
  LaserWeedJobConfiguration,
  State,
} from "protos/rtc/jobs";
import {
  Direction,
  directionFromJSON,
  PathPlanConfiguration,
} from "protos/rtc/path_plan";
import { Farm } from "protos/farm/farm";
import { farmApi } from "common/state/portalApi/farm";
import { Field, Form, Formik } from "formik";
import { Select as FormikSelect, Switch } from "formik-mui";
import { isAtkLike } from "common/utils/robots";
import { JobType, jobTypeFromJSON } from "protos/atk/job_type";
import { LocalUnitField } from "portal/components/LocalUnitField";
import { portalApi } from "portal/state/portalApi";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { titleCase } from "portal/utils/strings";
import { typedPath } from "typed-path";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import React, { FC, ReactNode, useCallback, useState } from "react";

interface SetJobFormProps {
  initialJob: Job;
  initialSelectedRobotSerial?: string;
  initialLaserWeedConfig?: LaserWeedJobConfiguration;
  initialGroundPrepConfig?: GroundPrepJobConfiguration;
  mode: "new-job" | "edit-pending" | "edit-active";
  onSubmit: (job: CreateJobRequest) => Promise<void>;
  onCancel: () => void;
}

interface SetJobFormState {
  name: string;
  // MUI only special-cases "" for an invalid sentinel value in selects; if we
  // use the more obvious JobType.JOB_TYPE_UNSPECIFIED, the styling on the
  // select box breaks and it yells at us in console.warn
  type: JobType | "";
  farmId: string;
  fieldId: string;
  laserWeed: {
    rowSpacingM: number;
  };
  groundPrep: {
    robotSerial: string;
    rigId: string;
    pathPlan: Omit<
      PathPlanConfiguration,
      | "splitOnCenterPivots"
      | "toolSpeedMS"
      | "transitionSpeedMS"
      | "numHeadlandPasses"
      | "doHeadlandsFirst"
    > & {
      ignoreCenterPivots: boolean;
      workHeadlandsWhen: "none" | "before" | "after";
    };
  };
}

const S = typedPath<SetJobFormState>();

function initialFormStateFromExistingJob(
  job: Job,
  initialSelectedRobotSerial: string | undefined,
  laserWeedConfig: LaserWeedJobConfiguration | undefined,
  groundPrepConfig: GroundPrepJobConfiguration | undefined
): SetJobFormState {
  const defaultLaserWeedState: SetJobFormState["laserWeed"] = {
    rowSpacingM: 6.1, // 20 feet, rounded to the nearest centimeter
  };

  const defaultGroundPrepState: SetJobFormState["groundPrep"] = {
    robotSerial: "",
    rigId: "",
    pathPlan: {
      activities: undefined,
      rigActivities: undefined,
      enableDebugRun: false,
      doHeadlandFirst: true,
      ignoreCenterPivots: false,
      rowHeadingDeg: 45,
      headlandWidthM: 6,
      turnDirection: Direction.CW,
      flipRowOrder: false,
      workingOverlapM: 0,
      workHeadlandsWhen: "none",
    },
  };

  if (laserWeedConfig) {
    return {
      name: job.name,
      type: job.type,
      farmId: job.farmId,
      fieldId: job.fieldId,
      laserWeed: {
        rowSpacingM: laserWeedConfig.rowSpacingM,
      },
      groundPrep: defaultGroundPrepState,
    };
  } else if (groundPrepConfig) {
    let workHeadlandsWhen: "before" | "after" | "none";

    // convert wire object headland configuration to form configuration
    if (groundPrepConfig.pathPlan) {
      if (groundPrepConfig.pathPlan.numHeadlandPasses > 0) {
        workHeadlandsWhen = groundPrepConfig.pathPlan.doHeadlandFirst
          ? "before"
          : "after";
      } else {
        workHeadlandsWhen = "none";
      }
    } else {
      workHeadlandsWhen = defaultGroundPrepState.pathPlan.workHeadlandsWhen;
    }

    return {
      name: job.name,
      type: job.type,
      farmId: job.farmId,
      fieldId: job.fieldId,
      laserWeed: defaultLaserWeedState,
      groundPrep: {
        robotSerial: initialSelectedRobotSerial ?? "",
        rigId:
          groundPrepConfig.rigId === uuid.NIL
            ? defaultGroundPrepState.rigId
            : groundPrepConfig.rigId,
        pathPlan: {
          ...(groundPrepConfig.pathPlan ?? defaultGroundPrepState.pathPlan),
          ignoreCenterPivots: groundPrepConfig.pathPlan
            ? !groundPrepConfig.pathPlan.splitOnCenterPivots
            : defaultGroundPrepState.pathPlan.ignoreCenterPivots,
          workHeadlandsWhen,
        },
      },
    };
  } else {
    return {
      name: job.name,
      type: job.type,
      farmId: job.farmId,
      fieldId: job.fieldId,
      laserWeed: defaultLaserWeedState,
      groundPrep: defaultGroundPrepState,
    };
  }
}

function createJobRequestFromFormState(
  state: SetJobFormState,
  farms: Farm[] | undefined,
  existingCustomerId: string
): CreateJobRequest {
  if (state.type === JobType.LASER_WEED) {
    return {
      name: state.name,
      type: state.type,
      farmId: state.farmId,
      customerId: existingCustomerId,
      fieldId: state.fieldId,
      laserWeed: {
        rowSpacingM: state.laserWeed.rowSpacingM,
        updatedAt: undefined, // this parameter is only written by the jobs service, never read
      },
      priority: 0,
      generateObjectives: true,
      dryRun: false,
    };
  } else if (state.type === JobType.GROUND_PREP) {
    // clean up the path plan for the server since some of our UI presents
    // information in a different way from the internal representation
    const { ignoreCenterPivots: _icp, ...restOfPathPlan } =
      state.groundPrep.pathPlan;
    const pathPlan: PathPlanConfiguration = {
      ...restOfPathPlan,
      doHeadlandFirst: state.groundPrep.pathPlan.workHeadlandsWhen === "before",
      // HACK: for "non-legacy" headlands, this value needs to have a zero
      // value and a value that activates the new headland code's
      // auto-compute behavior.  this came in very hot in M5 and for time
      // reasons, this is activated by the magic number 273 pending a proto
      // change. (ColinB, 2/5/26)
      numHeadlandPasses:
        state.groundPrep.pathPlan.workHeadlandsWhen === "none" ? 0 : 273,
      // our UI has the opposite polarity from the API parameter
      splitOnCenterPivots: !state.groundPrep.pathPlan.ignoreCenterPivots,
      enableDebugRun: false,
      // the rest of the parameters on the path plan are deprecated; while we
      // wait for them to be removed entirely, pass conservative values to
      // break path planning + cause the tractor to crawl so we break things
      // (code) while not breaking things (physical).
      toolSpeedMS: 0.1,
      transitionSpeedMS: 0.1,
    };

    return {
      name: state.name,
      type: state.type,
      farmId: state.farmId,
      customerId: existingCustomerId,
      fieldId: state.fieldId,
      groundPrep: {
        rigId: state.groundPrep.rigId,
        pathPlan,
        // parameters only written by the jobs service, never read
        rigSnapshotId: "",
        updatedAt: undefined,
      },
      priority: 0,
      generateObjectives: true,
      dryRun: false,
    };
  } else {
    throw new Error("createJobRequestFromFormState: invalid job type?");
  }
}

const LoadingModal: FC = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-row justify-center">
      <CircularProgress variant="indeterminate" className="mr-2" />
      <Typography className="text-3xl">
        {t("components.Loading.placeholder")}
      </Typography>
    </div>
  );
};

const EditJobForm: FC<SetJobFormProps> = ({
  onSubmit,
  onCancel,
  initialJob,
  initialSelectedRobotSerial,
  initialLaserWeedConfig,
  initialGroundPrepConfig,
  mode,
}) => {
  const showFarm = mode === "new-job";
  const jobActive = mode === "edit-active";
  const { data: farms, isLoading: farmsLoading } =
    farmApi.endpoints.listFarms.useQuery(
      {
        contents: "no-geometry",
      },
      { skip: jobActive }
    );
  const { data: robots, isLoading: robotsLoading } =
    portalApi.endpoints.listRobots.useQuery({});
  const { t } = useTranslation();

  const validationSchema = yup.object({
    type: yup
      .mixed<JobType>()
      .oneOf(Object.values(JobType).map((t) => jobTypeFromJSON(t)))
      .required(),
    farmId: yup.string().required(),
    fieldId: yup.string().required(),
    name: yup.string().required(),
    groundPrep: yup.object().when("type", {
      is: JobType.GROUND_PREP,
      // eslint-disable-next-line unicorn/no-thenable
      then: (schema) =>
        schema
          .shape({
            rigId: yup.string().not([""]),
            pathPlan: yup.object().shape({
              doHeadlandFirst: yup.boolean().required(),
              ignoreCenterPivots: yup.boolean().required(),
              rowHeadingDeg: yup.number().min(0).max(360).required(),
              headlandWidthM: yup.number().min(0).required(),
              turnDirection: yup
                .mixed<Direction>()
                .oneOf(
                  Object.values(Direction).map((t) => directionFromJSON(t))
                )
                .required(),
              numHeadlandPasses: yup.number().integer().min(0).required(),
              workingOverlapM: yup.number().min(0).required(),
            }),
          })
          .required(),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  });

  return farmsLoading || robotsLoading ? (
    <LoadingModal />
  ) : (
    <div className="flex flex-col gap-4 p-4">
      <Formik
        initialValues={initialFormStateFromExistingJob(
          initialJob,
          initialSelectedRobotSerial,
          initialLaserWeedConfig,
          initialGroundPrepConfig
        )}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          await onSubmit(
            createJobRequestFromFormState(values, farms, initialJob.customerId)
          );
          setSubmitting(false);
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          submitForm,
          handleReset,
          isSubmitting,
          isValid,
          setFieldTouched,
          setFieldValue,
          validateForm,
          dirty,
        }) => {
          const {
            data: listRigsForRobotResponse,
            isLoading: listRigsForRobotLoading,
          } = atkEquipmentApi.endpoints.searchRigs.useQuery(
            {
              serial: values.groundPrep.robotSerial,
              validate: true,
            },
            { skip: !values.groundPrep.robotSerial }
          );

          return (
            <Form className="flex flex-col gap-4">
              <FormControl fullWidth>
                <InputLabel id="name-label">
                  {titleCase(t("utils.descriptors.name"))}
                </InputLabel>
                <Input
                  id="name"
                  type="string"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="type-label">
                  {titleCase(t("utils.descriptors.type_one"))}
                </InputLabel>
                <Select
                  title={titleCase(t("utils.descriptors.type_one"))}
                  labelId="type-label"
                  id="type-selector"
                  name={S.type.$path}
                  value={values.type}
                  label={titleCase(t("utils.descriptors.type_one"))}
                  disabled={mode !== "new-job"}
                  onChange={(e) => {
                    // work around https://github.com/jaredpalmer/formik/issues/1191
                    // and https://github.com/jaredpalmer/formik/issues/2083
                    setFieldValue("type", e.target.value).finally(() =>
                      setFieldTouched("type", true)
                    );
                    validateForm();
                  }}
                >
                  <MenuItem value={JobType.LASER_WEED}>
                    {t("models.autotractor.jobTypes.laserWeed")}
                  </MenuItem>
                  <MenuItem value={JobType.GROUND_PREP}>
                    {t("models.autotractor.jobTypes.groundPrep")}
                  </MenuItem>
                </Select>
              </FormControl>
              {jobActive && (
                <Alert severity="warning">
                  {t("views.autotractor.jobDetails.jobActiveEditingLimited")}
                </Alert>
              )}
              {showFarm && (
                <FormControl fullWidth>
                  <Field
                    component={FormikSelect}
                    title={titleCase(t("models.farms.farm_one"))}
                    id="farmId"
                    name={S.farmId.$path}
                    value={values.farmId}
                    label={titleCase(t("models.farms.farm_one"))}
                    onChange={handleChange}
                  >
                    {farms?.map((f) => (
                      <MenuItem key={f.id?.id} value={f.id?.id}>
                        {f.name}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
              )}
              {!jobActive && (
                <FormControl fullWidth>
                  <Field
                    component={FormikSelect}
                    disabled={!values.farmId}
                    title={titleCase(t("models.farms.zone_one"))}
                    id="fieldId"
                    name={S.fieldId.$path}
                    label={titleCase(t("models.farms.zone_one"))}
                  >
                    {values.farmId &&
                      farms
                        ?.find((f) => f.id?.id === values.farmId)
                        ?.zones.filter((z) => Boolean(z.contents?.field)) // todo: should we allow jobs on non-fields?
                        .map((z) => (
                          <MenuItem key={z.id?.id} value={z.id?.id}>
                            {z.name}
                          </MenuItem>
                        ))}
                  </Field>
                </FormControl>
              )}
              {values.type === JobType.LASER_WEED && (
                <FormControl className="flex flex-col gap-4">
                  <InputLabel>
                    {t("models.pathPlanning.laserWeed.rowSpacing")}
                  </InputLabel>
                  <LocalUnitField
                    name={S.laserWeed.rowSpacingM.$path}
                    storedUnit="m"
                    displayUnits={{ imperial: "ft", metric: "m" }}
                  >
                    {({ name, value, onChange, displayUnit }) => (
                      <Input
                        type="number"
                        disabled={jobActive}
                        name={name}
                        value={value}
                        onChange={onChange}
                        onBlur={handleBlur}
                        inputProps={{ min: 0, step: 0.01 }}
                        endAdornment={displayUnit}
                      />
                    )}
                  </LocalUnitField>
                </FormControl>
              )}
              {values.type === JobType.GROUND_PREP && (
                <FormControl className="flex flex-col gap-4">
                  <FormControl fullWidth>
                    <InputLabel id="robot-label">
                      {titleCase(t("models.robots.robot", { count: 1 }))}
                    </InputLabel>
                    <Select
                      title={titleCase(t("models.robots.robot", { count: 1 }))}
                      labelId="robot-label"
                      label={titleCase(t("models.robots.robot", { count: 1 }))}
                      disabled={jobActive}
                      name={S.groundPrep.robotSerial.$path}
                      value={values.groundPrep.robotSerial}
                      onChange={handleChange}
                    >
                      <MenuItem value="">
                        {t("utils.descriptors.none")}
                      </MenuItem>
                      {robots &&
                        robots
                          .filter((robot) => isAtkLike(robot.robot))
                          .map(
                            (robot, index) =>
                              robot.robot && (
                                <MenuItem
                                  key={index}
                                  value={robot.robot.serial}
                                >
                                  {robot.robot.customerSerial}
                                </MenuItem>
                              )
                          )}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="rig-label">
                      {titleCase(t("models.equipment.rig", { count: 1 }))}
                    </InputLabel>
                    <Select
                      title={titleCase(t("models.equipment.rig", { count: 1 }))}
                      labelId="rig-label"
                      label={titleCase(t("models.equipment.rig", { count: 1 }))}
                      disabled={jobActive}
                      name={S.groundPrep.rigId.$path}
                      value={values.groundPrep.rigId}
                      onChange={handleChange}
                    >
                      <MenuItem value="">
                        {t("utils.descriptors.none")}
                      </MenuItem>
                      {listRigsForRobotResponse &&
                        listRigsForRobotResponse.rigs.map((rig, index) => {
                          const validationResults =
                            listRigsForRobotResponse.validationResults[rig.id];

                          if (
                            validationResults &&
                            validationResults.errors.length > 0
                          ) {
                            return (
                              <MenuItem
                                key={index}
                                value={rig.id}
                                disabled={true}
                              >
                                <Typography>
                                  {rig.customerProvidedName === ""
                                    ? t("models.equipment.noName")
                                    : rig.customerProvidedName}

                                  <Typography variant="body2" className="ml-2">
                                    {t("models.pathPlanning.rigNotReady")}
                                  </Typography>
                                </Typography>
                              </MenuItem>
                            );
                          } else {
                            return (
                              <MenuItem key={index} value={rig.id}>
                                {rig.customerProvidedName === ""
                                  ? t("models.equipment.noName")
                                  : rig.customerProvidedName}
                              </MenuItem>
                            );
                          }
                        })}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <Field
                      component={FormikSelect}
                      name={S.groundPrep.pathPlan.workHeadlandsWhen.$path}
                      disabled={jobActive}
                      // work around alignment sloppiness from #4607
                      formControl={{ className: "mt-0" }}
                      label={t(
                        "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.workHeadlandsWhen"
                      )}
                    >
                      <MenuItem value="none">
                        {t(
                          "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.workHeadlandsNone"
                        )}
                      </MenuItem>
                      <MenuItem value="before">
                        {t(
                          "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.workHeadlandsBefore"
                        )}
                      </MenuItem>
                      <MenuItem value="after">
                        {t(
                          "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.workHeadlandsAfter"
                        )}
                      </MenuItem>
                    </Field>
                  </FormControl>
                  <FormControl fullWidth className="pb-2 pl-2">
                    <FormControlLabel
                      control={
                        <Field
                          component={Switch}
                          disabled={jobActive}
                          type="checkbox"
                          name={S.groundPrep.pathPlan.ignoreCenterPivots.$path}
                        />
                      }
                      label={
                        <Typography variant="body1">
                          {t("models.pathPlanning.ignoreCenterPivots.title")}
                          <Typography variant="body2">
                            {t("models.pathPlanning.ignoreCenterPivots.detail")}
                          </Typography>
                        </Typography>
                      }
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="turn-direction-label">
                      {titleCase(t("models.pathPlanning.turnDirection"))}
                    </InputLabel>
                    <Select
                      value={values.groundPrep.pathPlan.turnDirection}
                      disabled={jobActive}
                      labelId="turn-direction-label"
                      name={S.groundPrep.pathPlan.turnDirection.$path}
                      title={t("models.pathPlanning.turnDirection")}
                      label={t("models.pathPlanning.turnDirection")}
                      onChange={(e) => {
                        // work around https://github.com/jaredpalmer/formik/issues/1191
                        // and https://github.com/jaredpalmer/formik/issues/2083
                        setFieldValue(
                          "groundPrep.pathPlan.turnDirection",
                          e.target.value
                        ).finally(() =>
                          setFieldTouched(
                            "groundPrep.pathPlan.turnDirection",
                            true
                          )
                        );
                        validateForm();
                      }}
                    >
                      <MenuItem value={Direction.CW}>
                        {t("utils.units.cwLong")}
                      </MenuItem>
                      <MenuItem value={Direction.CCW}>
                        {t("utils.units.ccwLong")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>
                      {t("models.pathPlanning.rowHeading")}
                    </InputLabel>
                    <Input
                      type="number"
                      disabled={jobActive}
                      value={values.groundPrep.pathPlan.rowHeadingDeg}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name={S.groundPrep.pathPlan.rowHeadingDeg.$path}
                      inputProps={{ min: 0, max: 360, step: 0.1 }}
                      endAdornment={t("utils.units.degLong")}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>
                      {t("models.pathPlanning.headlandWidth")}
                    </InputLabel>
                    <LocalUnitField
                      name={S.groundPrep.pathPlan.headlandWidthM.$path}
                      storedUnit="m"
                      displayUnits={{ imperial: "ft", metric: "m" }}
                    >
                      {({ name, value, onChange, displayUnit }) => (
                        <Input
                          type="number"
                          disabled={jobActive}
                          value={value}
                          onChange={onChange}
                          onBlur={handleBlur}
                          name={name}
                          inputProps={{ min: 0, step: 0.05 }}
                          endAdornment={displayUnit}
                        />
                      )}
                    </LocalUnitField>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>
                      {t("models.pathPlanning.workingOverlapM")}
                    </InputLabel>
                    <LocalUnitField
                      name={S.groundPrep.pathPlan.workingOverlapM.$path}
                      storedUnit="m"
                      displayUnits={{ imperial: "ft", metric: "m" }}
                    >
                      {({ name, value, onChange, displayUnit }) => (
                        <Input
                          type="number"
                          disabled={jobActive}
                          value={value}
                          onChange={onChange}
                          onBlur={handleBlur}
                          name={name}
                          inputProps={{ min: 0, step: 0.05 }}
                          endAdornment={displayUnit}
                        />
                      )}
                    </LocalUnitField>
                  </FormControl>
                </FormControl>
              )}
              <div className="flex">
                <Button
                  fullWidth
                  variant="contained"
                  disabled={
                    !isValid ||
                    isSubmitting ||
                    !dirty ||
                    listRigsForRobotLoading
                  }
                  type="submit"
                  onClick={submitForm}
                >
                  {mode === "new-job"
                    ? t("utils.actions.create")
                    : t("utils.actions.save")}
                </Button>
                <Button
                  fullWidth
                  color="info"
                  onClick={() => {
                    onCancel();
                    handleReset();
                  }}
                >
                  {t("utils.actions.cancel")}
                </Button>
              </div>
              {isSubmitting && (
                <LinearProgress className="w-full" variant="indeterminate" />
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export function useEditJobDialog(initialJob: Job): {
  renderDialog: () => ReactNode;
  open: () => void;
  close: () => void;
} {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const [updateJob] = useMutationPopups(
    rtcJobsApi.endpoints.updateJob.useMutation()
  );
  const [updateJobConfig] = useMutationPopups(
    rtcJobsApi.endpoints.updateJobConfig.useMutation()
  );
  const { data: configuredJob, isLoading: configuredJobLoading } =
    rtcJobsApi.endpoints.getConfiguredJob.useQuery({
      id: initialJob.id.toString(),
    });
  const initialLaserWeedConfig = configuredJob?.laserWeed;
  const initialGroundPrepConfig = configuredJob?.groundPrep;

  // for ground prep jobs with a rig ID, we need to figure out which robot is
  // associated with the rig so we can select it in the robot selector.
  const { data: searchRigsResult } =
    atkEquipmentApi.endpoints.searchRigs.useQuery(
      {
        rigId: initialGroundPrepConfig?.rigId,
      },
      { skip: !initialGroundPrepConfig?.rigId }
    );

  // after a job has been readied, don't allow the user to change it in any way
  // that would necessitate the job objectives being regenerated.
  const mode =
    initialJob.state === State.PENDING ? "edit-pending" : "edit-active";

  const handleUpdateJob = useCallback(
    async (j: CreateJobRequest): Promise<void> => {
      const updatedJob = {
        ...initialJob,
        name: j.name,
        type: j.type,
        farmId: j.farmId,
        fieldId: j.fieldId,
      };

      const laserWeedConfigDirty =
        JSON.stringify(initialLaserWeedConfig) !== JSON.stringify(j.laserWeed);
      const groundPrepConfigDirty =
        JSON.stringify(initialGroundPrepConfig) !==
        JSON.stringify(j.groundPrep);
      const updateJobResult = await updateJob({
        job: updatedJob,
        generateObjectives:
          initialJob.state === State.PENDING &&
          j.fieldId !== initialJob.fieldId &&
          // only generate objectives now if we're not about to ask the path
          // planner to plan a path again
          !groundPrepConfigDirty,
      });

      if (updateJobResult.error) {
        return;
      }

      if (j.laserWeed && laserWeedConfigDirty && mode !== "edit-active") {
        const putLaserWeedConfigResult = await updateJobConfig({
          id: updatedJob.id,
          generateObjectives: true,
          config: {
            laserWeed: j.laserWeed,
            jobId: updatedJob.id,
          },
        });

        if (!putLaserWeedConfigResult.error) {
          setIsOpen(false);
        }
      } else if (
        j.groundPrep &&
        groundPrepConfigDirty &&
        mode !== "edit-active"
      ) {
        const putGroundPrepConfigResult = await updateJobConfig({
          id: updatedJob.id,
          generateObjectives: true,
          config: { groundPrep: j.groundPrep, jobId: updatedJob.id },
        });

        if (!putGroundPrepConfigResult.error) {
          setIsOpen(false);
        }
      } else {
        setIsOpen(false);
      }
    },
    [
      initialGroundPrepConfig,
      initialJob,
      initialLaserWeedConfig,
      updateJob,
      updateJobConfig,
      mode,
    ]
  );

  const handleCancelJob = useCallback((): void => {
    setIsOpen(false);
  }, []);

  return {
    renderDialog: () => (
      <Dialog open={isOpen} fullWidth>
        <DialogTitle>
          {titleCase(
            t("utils.actions.editLong", {
              subject: t("models.jobs.job_one"),
            })
          )}
        </DialogTitle>
        <DialogContent>
          {configuredJobLoading ? (
            <LoadingModal />
          ) : (
            <EditJobForm
              initialJob={initialJob}
              initialSelectedRobotSerial={searchRigsResult?.rigs[0]?.serial}
              initialLaserWeedConfig={initialLaserWeedConfig}
              initialGroundPrepConfig={initialGroundPrepConfig}
              onCancel={handleCancelJob}
              onSubmit={handleUpdateJob}
              mode={mode}
            />
          )}
        </DialogContent>
      </Dialog>
    ),
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  };
}
